import SelectComponent from "@via/components/select/select.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useCallback, useEffect, useState } from "react";
import { User } from "common";
import { useGetUsers } from "@via/pages/account/admin/admin.api.tsx";
import { useDebounceCallback } from "usehooks-ts";
import { FilterItem } from "@via/pages/account/admin/utils/helper.ts";

export default function ImpersonationSelectComponent() {
  const { setImpersonatedUserId } = useGlobal();
  const [searchedInput, setSearchedInput] = useState("");

  const [cumulativeUsers, setCumulativeUsers] = useState<User[]>([]);
  const [usersPage, setUsersPage] = useState(0);
  const userOptionSize = 10;

  const userFilters = [
    {
      id: "username",
      value: { operator: "like", term: searchedInput || "" },
    } as FilterItem,
  ];

  const { data: users, isLoading: isUsersLoading } = useGetUsers({
    take: userOptionSize,
    skip: usersPage * userOptionSize,
    filter: userFilters,
  });

  const fetchMoreUsers = useCallback(() => {
    setUsersPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((users?.total ?? 0) / userOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [users?.total, userOptionSize]);

  useEffect(() => {
    if (users?.data) {
      setCumulativeUsers([...cumulativeUsers, ...users.data]);
    }
  }, [users?.data]);

  const handleUserChange = async (selectedOption: SelectOption) => {
    if (selectedOption?.value) {
      setImpersonatedUserId(Number(selectedOption.value));
    } else {
      setImpersonatedUserId(null);
    }
  };

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setCumulativeUsers([]);
    setUsersPage(0);
    setSearchedInput(value);
  }, 300);

  return (
    <form className="flex justify-end">
      <div className="flex flex-wrap items-center gap-3">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Choose User:
        </label>
        <div className="w-70">
          <SelectComponent
            name="userId"
            options={
              cumulativeUsers?.map(({ userName, id }) => ({
                label: userName,
                value: id,
              })) ?? []
            }
            isLoading={isUsersLoading}
            placeholder={"Username"}
            onFetchMoreOptions={fetchMoreUsers}
            onChange={data => {
              handleUserChange(data as SelectOption);
            }}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
          />
        </div>
      </div>
    </form>
  );
}
